import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Field } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import formStyles from '../../../styles/forms.module.scss';
import s from './WindFacilityForm.module.scss';
import {
  TextInput,
  CheckboxInput,
  AccessibleDatepickerInput,
  AddressInputFields,
  CommonFacilityFields,
} from '../../../common/FormFields';
import RemoveFacilityButton from '../FacilityButtons/RemoveFacilityButton';
import InfoBubble from '../../../common/InfoBubble/InfoBubble';
import DuplicateFacilityButton from '../FacilityButtons/DuplicateFacilityButton';
import type { WindFacilityField } from '../../../domain/facility';
import type { WindFacilityFormProps } from './facilityFormTypes';

const NAME_PREFIX_MIN = 'windFacilityData' as const;

function WindFacilityForm({
  id,
  index,
  deleteFacility,
  values,
  form,
  arrayHelpers,
}: WindFacilityFormProps) {
  const namePrefix = `${NAME_PREFIX_MIN}[${index}]`;
  const fieldName = (field: WindFacilityField) => `${namePrefix}.${field}`;

  const firstInputRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (index !== 0 && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [index]);

  const duplicateFacility = () => {
    const facilities = values[NAME_PREFIX_MIN];
    if (facilities?.[index]) {
      arrayHelpers.insert(index + 1, {
        ...facilities[index],
        id: uuidv4(),
      });
    }
  };

  return (
    <div className={formStyles.inputGroup}>
      {index !== 0 && (
        <>
          <hr className={formStyles.separator} />
          <div className={s.removeWrapper}>
            <RemoveFacilityButton onClick={() => deleteFacility(index)} />
          </div>
        </>
      )}
      <fieldset>
        <legend className={formStyles.caption}>
          <FormattedMessage id="offerWizard.StepFacilityData.technicalDetails" />
        </legend>
        <div ref={firstInputRef} tabIndex={-1}>
          <div data-id={id} className={formStyles.inputPair}>
            <Field
              name={fieldName('hubHeight')}
              component={TextInput}
              labelMsg="offerWizard.StepWindFacilityData.hubHeight.label"
            />
            <Field
              name={fieldName('diameter')}
              component={TextInput}
              labelMsg="offerWizard.StepWindFacilityData.diameter"
            />
          </div>
        </div>
        <div className={formStyles.inputPair}>
          <Field
            name={fieldName('output')}
            component={TextInput}
            labelMsg="offerWizard.StepWindFacilityData.output"
          />
          <Field
            name={fieldName('powerDensity')}
            component={TextInput}
            labelMsg="offerWizard.StepWindFacilityData.powerDensity"
          />
        </div>
        <div className={formStyles.inputPair}>
          <Field
            name={fieldName('manufacturer')}
            component={TextInput}
            labelMsg="offerWizard.StepWindFacilityData.manufacturer"
          />
          <Field
            name={fieldName('model')}
            component={TextInput}
            labelMsg="offerWizard.StepWindFacilityData.model"
          />
        </div>
        <div className={formStyles.inputPairSingle}>
          <Field
            name={fieldName('initialOperation')}
            component={AccessibleDatepickerInput}
            labelMsg="offerWizard.StepWindFacilityData.initialOperation.label"
          />
        </div>
        <div className={formStyles.inputWithVerticalSpacing}>
          <div className={s.checkBoxWithInfoIcon}>
            <CheckboxInput
              name={fieldName('paragraph51')}
              labelContent={
                <FormattedMessage id="offerWizard.StepWindFacilityData.paragraph51" />
              }
            />
            <div className={s.infoIcon}>
              <InfoBubble>
                <FormattedMessage id="offerWizard.StepWindFacilityData.paragraph51.text" />
              </InfoBubble>
            </div>
          </div>
          <CheckboxInput
            name={fieldName('paymentNegativeHours')}
            labelContent={
              <FormattedMessage id="offerWizard.StepWindFacilityData.paymentNegativeHours" />
            }
          />
        </div>
      </fieldset>
      <CommonFacilityFields
        namePrefix={namePrefix}
        facilityValues={values.windFacilityData?.[index]}
        form={form}
      />
      <div className={formStyles.inputWithVerticalSpacing}>
        <fieldset>
          <legend className={formStyles.caption}>
            <FormattedMessage id="offerWizard.StepWindFacilityData.address" />
          </legend>
          <AddressInputFields namePrefix={namePrefix} isRequired={false} />
        </fieldset>
      </div>
      <DuplicateFacilityButton onClick={() => duplicateFacility()} />
    </div>
  );
}

export default WindFacilityForm;
